import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useShellUI, ShellRail } from '../../shell/AppShell';
import { orphanMarketplaceAPI, startupMarketplaceAPI } from '../../utils/api';

/**
 * ApplicationsConsole — the lister's pipeline at the EXISTING
 * /orphan-marketplace/:id/applications and /startup-marketplace/:id/applications
 * paths (Postmark emails link here). Review applicants, decline with a note,
 * or accept (orphan accept = markAdopted: hands over the team and closes the
 * listing — guarded by an inline confirm, never a browser dialog).
 */

const asArray = (data, ...keys) => {
  if (Array.isArray(data)) return data;
  for (const key of keys) {
    if (Array.isArray(data?.[key])) return data[key];
  }
  return [];
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
};

const STATUS_CHIP = {
  pending: ['Pending', 'ft-chip'],
  accepted: ['Accepted', 'ft-chip green'],
  rejected: ['Declined', 'ft-chip'],
};

const ResumeChips = ({ resume }) => {
  if (!resume || typeof resume !== 'object') return null;
  const chips = [];
  if (resume.leagues ?? resume.totalLeagues) chips.push(`${resume.leagues ?? resume.totalLeagues} leagues`);
  if (resume.winRate ?? resume.winPct) chips.push(`${resume.winRate ?? resume.winPct}% win rate`);
  if (resume.championships != null) chips.push(`${resume.championships} title${resume.championships === 1 ? '' : 's'}`);
  if (resume.trades ?? resume.totalTrades) chips.push(`${resume.trades ?? resume.totalTrades} trades`);
  if (chips.length === 0) return null;
  return (
    <div className="ft-pillrow" style={{ margin: '8px 0' }}>
      {chips.map((c) => <span key={c} className="ft-chip outline">{c}</span>)}
    </div>
  );
};

const ApplicationCard = ({ app, type, onDecline, onAccept, busy }) => {
  const [mode, setMode] = useState('view'); // view | declining | confirmAccept
  const [note, setNote] = useState('');
  const [statusLabel, chipClass] = STATUS_CHIP[app.status] || [app.status, 'ft-chip outline'];
  const actionable = app.status === 'pending';

  return (
    <div className="ft-card" style={{ marginBottom: 14, maxWidth: 640 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 38, height: 38, borderRadius: '50%', background: 'var(--ft-surface-hover)', flex: 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13,
          overflow: 'hidden', color: 'var(--ft-ink-2)',
        }}>
          {app.avatarUrl ? (
            <img src={app.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          ) : (app.sleeperUsername || '?').slice(0, 2).toUpperCase()}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 14.5, fontWeight: 600 }}>{app.sleeperUsername}</div>
          <div style={{ fontSize: 11.5, color: 'var(--ft-ink-3)' }}>Applied {fmtDate(app.createdAt)}</div>
        </div>
        <span className={chipClass}>{statusLabel}</span>
      </div>
      <ResumeChips resume={app.dynastyResume} />
      {app.message ? (
        <div style={{ fontSize: 13.5, color: 'var(--ft-ink-2)', lineHeight: 1.5, margin: '10px 0 0' }}>
          "{app.message}"
        </div>
      ) : null}
      {app.status === 'rejected' && app.declineNote ? (
        <div style={{ fontSize: 12.5, color: 'var(--ft-ink-3)', marginTop: 8 }}>Your note: {app.declineNote}</div>
      ) : null}
      {actionable && (
        <div style={{ marginTop: 16 }}>
          {mode === 'view' && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="ft-btn sm" disabled={busy} onClick={() => setMode('confirmAccept')}>Accept</button>
              <button className="ft-btn sm ghost" disabled={busy} onClick={() => setMode('declining')}>Decline</button>
              <button
                className="ft-btn sm ghost"
                onClick={() => window.open(`/manager-intel/${app.sleeperUsername}`, '_self')}
              >
                Scout
              </button>
            </div>
          )}
          {mode === 'confirmAccept' && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, color: 'var(--ft-ink-2)' }}>
                {type === 'orphan' ? 'Hands over the team and closes the listing.' : 'Locks their spot in the league.'}
              </span>
              <button className="ft-btn sm" disabled={busy} onClick={() => onAccept(app)}>
                {busy ? 'Working…' : 'Confirm handover'}
              </button>
              <button className="ft-btn sm ghost" onClick={() => setMode('view')}>Cancel</button>
            </div>
          )}
          {mode === 'declining' && (
            <div>
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="A note softens the blow — optional"
                style={{ width: '100%', background: '#fff', border: '1px solid var(--ft-hairline)', borderRadius: 'var(--ft-r-input)', padding: '11px 14px', fontSize: 13.5, outline: 'none' }}
              />
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button className="ft-btn sm" disabled={busy} onClick={() => onDecline(app, note)}>
                  {busy ? 'Sending…' : 'Send decline'}
                </button>
                <button className="ft-btn sm ghost" onClick={() => setMode('view')}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ApplicationsConsole = ({ type }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useShellUI();
  const api = type === 'orphan' ? orphanMarketplaceAPI : startupMarketplaceAPI;
  const basePath = type === 'orphan' ? '/orphan-marketplace' : '/startup-marketplace';

  const [listing, setListing] = useState(null);
  const [apps, setApps] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.getById(id).then((res) => setListing(res.data?.orphan || res.data)).catch(() => {});
    api.getApplications(id).then((res) => setApps(asArray(res.data, 'applications'))).catch(() => setApps([]));
  }, [api, id]);

  useEffect(() => { load(); }, [load]);

  const decline = async (app, note) => {
    setBusy(true);
    try {
      await api.rejectApplication(id, app.id, note.trim() || undefined);
      toast(`Declined — ${app.sleeperUsername} gets an email`);
      load();
    } catch (err) {
      toast(err?.response?.data?.error || 'Decline failed — try again');
    } finally {
      setBusy(false);
    }
  };

  const accept = async (app) => {
    setBusy(true);
    try {
      if (type === 'orphan') {
        await api.markAdopted(id, { selectedApplicationId: app.id });
        toast(`Adopted — ${app.sleeperUsername} gets the join details`);
      } else {
        await api.acceptApplication(id, app.id);
        toast(`Accepted — ${app.sleeperUsername} gets the join details`);
      }
      load();
    } catch (err) {
      toast(err?.response?.data?.error || 'Accept failed — try again');
    } finally {
      setBusy(false);
    }
  };

  const pending = (apps || []).filter((a) => a.status === 'pending');
  const decided = (apps || []).filter((a) => a.status !== 'pending');

  return (
    <div className="ft-view">
      <Helmet><title>Applications — {listing?.leagueName || 'Listing'} — Fantasy Taco</title></Helmet>

      <ShellRail>
        {listing && (
          <div className="ft-railsec">
            <span className="ft-k">This listing</span>
            <div className="ft-card" style={{ marginTop: 10 }}>
              <div style={{ fontSize: 15, fontWeight: 600 }}>{listing.leagueName}</div>
              <div style={{ fontSize: 12.5, color: 'var(--ft-ink-3)', marginTop: 3 }}>
                {listing.teamName ? `${listing.teamName} · ` : ''}{listing.status === 'active' ? 'Live' : 'Closed'}
              </div>
            </div>
          </div>
        )}
        <div className="ft-railsec">
          <span className="ft-k">Pipeline</span>
          <div className="ft-progressrow"><span>Pending</span><span className={`ft-chip${pending.length ? ' brand' : ''}`}>{pending.length}</span></div>
          <div className="ft-progressrow"><span>Decided</span><span className="ft-chip outline">{decided.length}</span></div>
        </div>
      </ShellRail>

      <button className="ft-btn sm ghost" style={{ marginBottom: 22 }} onClick={() => navigate(`${basePath}/${id}`)}>
        Back to the listing
      </button>

      <span className="ft-k brand" style={{ display: 'block' }}>Marketplaces</span>
      <h1 className="ft-hero" style={{ fontSize: 28 }}>
        {listing ? `Applications — ${listing.leagueName}` : 'Applications'}
      </h1>
      <p className="ft-sub">Every applicant brings their dynasty resume. Scout before you decide.</p>

      <div className="ft-section">
        {apps === null ? (
          <div className="ft-loading">Pulling applications…</div>
        ) : apps.length === 0 ? (
          <p className="ft-sub">None yet. New applications land here and on Home.</p>
        ) : (
          <>
            {pending.length > 0 && (
              <>
                <span className="ft-k">Waiting on you</span>
                <div style={{ marginTop: 12 }}>
                  {pending.map((a) => (
                    <ApplicationCard key={a.id} app={a} type={type} busy={busy} onDecline={decline} onAccept={accept} />
                  ))}
                </div>
              </>
            )}
            {decided.length > 0 && (
              <div style={{ marginTop: pending.length ? 28 : 0 }}>
                <span className="ft-k">Decided</span>
                <div style={{ marginTop: 12 }}>
                  {decided.map((a) => (
                    <ApplicationCard key={a.id} app={a} type={type} busy={busy} onDecline={decline} onAccept={accept} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ApplicationsConsole;
